/**
 * Talking to the dissertation backend.
 *
 * The API answers in one of two shapes and the caller has to cope with both:
 * an MP3 body with the details in headers when speech synthesis worked, and
 * JSON when it did not. That is deliberate on the server side, so that losing
 * the voice never means losing the answer.
 */

/**
 * Where the backend lives.
 *
 * Written here as a plain constant rather than an environment variable on
 * purpose. It is only an address, it is public the moment the page loads, and
 * keeping it out of the build environment means Netlify's secret scanning has
 * nothing to find. Setting REACT_APP_API_BASE in Netlify would put the same
 * string in the build output as an environment variable value, which is the
 * shape that scanner fails builds on.
 *
 * Override it locally with REACT_APP_API_BASE in a .env.local file, which is
 * git-ignored, when running the backend on your own machine.
 */
const DEFAULT_API_BASE = 'https://tue026001.tail686332.ts.net';

const API_BASE = (
  process.env.REACT_APP_API_BASE || DEFAULT_API_BASE
).replace(/\/$/, '');

export class AskError extends Error {
  constructor(message, { kind = 'error', retryAfter = null, status = 0 } = {}) {
    super(message);
    this.name = 'AskError';
    this.kind = kind;
    this.retryAfter = retryAfter;
    this.status = status;
  }
}

/** Decode the answer text, which travels base64 because headers are Latin-1. */
function decodeHeaderText(value) {
  if (!value) return '';
  try {
    const binary = atob(value);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return '';
  }
}

export async function fetchSession(signal) {
  const response = await fetch(`${API_BASE}/api/session`, {
    method: 'POST',
    signal,
  });

  if (response.status === 429) {
    const body = await response.json().catch(() => ({}));
    throw new AskError(body.message || 'Too many requests.', {
      kind: 'rateLimited',
      retryAfter: body.retryAfter ?? null,
      status: 429,
    });
  }
  if (!response.ok) {
    throw new AskError('Could not reach the server.', {
      kind: 'network',
      status: response.status,
    });
  }

  return response.json();
}

/**
 * Send a question and get an answer back.
 *
 * `audio` is a Blob from the recorder, or `text` for the typed fallback used
 * in browsers that will not grant a microphone.
 */
export async function askQuestion({ token, audio, text, language, signal }) {
  const form = new FormData();

  if (audio) {
    // The filename extension matters. Speech recognition rejects an upload
    // whose type it cannot infer, and the browser's own blob type is the
    // only reliable source for it.
    form.append('audio', audio, `question.${extensionFor(audio.type)}`);
  } else if (text) {
    form.append('text', text);
  }
  if (language && language !== 'auto') form.append('language', language);

  const response = await fetch(`${API_BASE}/api/ask`, {
    method: 'POST',
    headers: {
      'X-Session-Token': token,
      // Sent as a header, not in the form, because the server decides whether
      // to refuse this request before it reads the body. Without it, a
      // rate-limited visitor would be turned away in English regardless of
      // the language they chose.
      'X-Preferred-Language': language && language !== 'auto' ? language : '',
    },
    body: form,
    signal,
  });

  if (response.status === 429) {
    const body = await response.json().catch(() => ({}));
    throw new AskError(body.message || 'Too many questions right now.', {
      kind: 'rateLimited',
      retryAfter: body.retryAfter ?? null,
      status: 429,
    });
  }
  if (response.status === 503) {
    throw new AskError('Busy right now. Try again in a moment.', {
      kind: 'busy',
      status: 503,
    });
  }
  if (response.status === 401) {
    throw new AskError('Session expired.', { kind: 'session', status: 401 });
  }
  if (response.status === 413) {
    throw new AskError('That recording is too long.', {
      kind: 'tooLong',
      status: 413,
    });
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new AskError(body.message || 'Something went wrong.', {
      kind: 'error',
      status: response.status,
    });
  }

  const contentType = response.headers.get('Content-Type') || '';

  if (contentType.includes('audio/')) {
    const blob = await response.blob();
    return {
      audio: blob,
      text: decodeHeaderText(response.headers.get('X-Answer-Text')),
      language: response.headers.get('X-Answer-Language') || 'en',
      kind: response.headers.get('X-Answer-Kind') || 'answer',
      spoken: true,
    };
  }

  const body = await response.json();
  return {
    audio: null,
    text: body.text || '',
    language: body.language || 'en',
    kind: body.kind || 'answer',
    spoken: false,
  };
}

function extensionFor(mimeType) {
  const base = String(mimeType || '').split(';')[0].trim().toLowerCase();
  if (base.includes('webm')) return 'webm';
  if (base.includes('ogg')) return 'ogg';
  if (base.includes('mp4') || base.includes('m4a')) return 'm4a';
  if (base.includes('wav')) return 'wav';
  if (base.includes('mpeg') || base.includes('mp3')) return 'mp3';
  return 'webm';
}

export { API_BASE };
